import React, { useCallback, useMemo } from 'react';
import type { Action, ActionFn, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import DataGrid, { defaultActions } from '#/common/components/DataGrid';
import columns from './columns';
import { actions } from './actions';
import { gql } from '@apollo/react-hooks';
import { getTenantList } from '#/graphql/queries';
import useTenantDelete from '../../hooks/useDeleteTenantMutation';
import useToast from '#/common/hooks/useToast';
import { Tenant } from '#/graphql/types';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: [],
  [UserRole.trane_admin]: [],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: [...defaultActions.map((a) => (typeof a === 'string' ? a : a.key))],
  [UserRole.trane_admin]: [...defaultActions.map((a) => (typeof a === 'string' ? a : a.key))],
  [UserRole.technician]: [...defaultActions.map((a) => (typeof a === 'string' ? a : a.key))],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Tenant>>> = {
  [UserRole.asset_manager]: { selectionMode: 'none' },
  [UserRole.trane_admin]: { selectionMode: 'none' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const getTenantListQuery = gql(getTenantList);

const TenantDataGrid: React.FC = () => {
  const { confirm } = useToast();

  const { deleteTenant } = useTenantDelete();

  const { user } = useSession();
  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete those tenants?`);
      if (!sure) {
        return;
      }
      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteTenant(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteTenant, confirm],
  );

  const tenantSelectedActions: ActionFn<Tenant>[] = useMemo(() => {
    const res: ActionFn<Tenant>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-model')) {
      res.push({
        key: 'delete-tenants',
        icon: 'trash',
        text: 'Delete',
        onClick: async (e) => {
          await deleteItem(
            e.selectedKeys.map((i: any) => i.id || i),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }

    return res;
  }, [role, deleteItem]);

  const gridActions = useMemo<Action<Tenant>[]>(() => {
    return actions
      .filter((a) => role && gridActionsPerRole[role]?.includes(typeof a === 'string' ? a : a.key))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, []);

  if (!role) return null;

  return (
    <DataGrid<Tenant>
      id="tenants"
      pluralText="Organizations"
      singularText="Organization"
      columns={columns}
      recordUrl="/admin/tenants/:id"
      actions={gridActions}
      selectedActions={tenantSelectedActions}
      getItemsQuery={getTenantListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(TenantDataGrid);
